// Вкладка «Категории» админ-консоли: список, форма редактирования, провайдеры,
// лимиты токенов категории. Выделено из admin.ts (аудит, этап 2 — декомпозиция).
import { state } from '../shared/state';
import { $, showToast } from '../shared/index';
import type { CategoryData } from '../shared/types';
import {
  TOKEN_LIMIT_MIN,
  TOKEN_LIMIT_STEP,
  type ApiErrorBody,
  setAdminCategoriesCache,
} from './admin-shared';

interface ProviderInfo {
  id: string;
  name: string;
  adapter?: string;
  defaultModel?: string;
  capabilities?: Record<string, unknown>;
}

let availableProviders: ProviderInfo[] = [];
let currentEditCategory = '';

function parseGroupedInt(value: string | number | null | undefined, fallback = 0) {
  const normalized = String(value ?? '').replace(/\s+/g, '');
  const n = parseInt(normalized, 10);
  return Number.isFinite(n) ? n : fallback;
}

function formatGroupedInt(value: string | number) {
  const n = parseGroupedInt(value, 0);
  return n.toLocaleString('ru-RU').replace(/\u00a0/g, ' ');
}

function bindGroupedNumberInput(id: string) {
  const el = $<HTMLInputElement>(id);
  if (!el || el.dataset.groupedNumberBound) return;
  el.dataset.groupedNumberBound = '1';
  el.addEventListener('input', () => {
    const digits = el.value.replace(/\D+/g, '');
    el.value = digits ? formatGroupedInt(digits) : '';
  });
  el.addEventListener('blur', () => {
    el.value = el.value ? formatGroupedInt(el.value) : '';
  });
}

function readCategoryTokenLimit(id: string, label: string, fallback: number) {
  const el = $<HTMLInputElement>(id);
  const value = parseGroupedInt(el?.value, fallback);
  if (!Number.isFinite(value) || value < TOKEN_LIMIT_MIN) {
    showToast(`❌ ${label} должен быть не меньше ${TOKEN_LIMIT_MIN} токенов`);
    return undefined;
  }
  if (value % TOKEN_LIMIT_STEP !== 0) {
    showToast(`❌ ${label} должен быть кратен ${TOKEN_LIMIT_STEP} токенам`);
    return undefined;
  }
  return value;
}

export async function loadAdminCategories() {
  const r = await fetch('/api/admin/categories', { headers: { 'Authorization': 'Bearer ' + state.authToken } });
  if (r.ok) {
    const cats = await r.json() as Record<string, CategoryData>;
    setAdminCategoriesCache(cats);
    const list = $('admin-category-list');
    if (!list) return;
    list.textContent = '';
    Object.entries(cats).forEach(([catName, data]) => {
      const providerLabel = data.provider ? data.provider.toUpperCase() : 'llamacpp';
      const tierLabel = data.retrieval_tier || 'consultant';
      const ragLabel = data.rag_allowed ? 'База знаний' : 'Без базы знаний';
      const inputDefault = formatGroupedInt(data.input_context_default ?? TOKEN_LIMIT_STEP);
      const inputMax = formatGroupedInt(data.input_context_max ?? TOKEN_LIMIT_STEP);
      const outputMax = formatGroupedInt(data.max_tokens ?? TOKEN_LIMIT_STEP);
      const el = document.createElement('div');
      el.className = 'user-item';
      el.innerHTML = `
        <div class="user-item-info">
          <span class="user-item-name">${DOMPurify.sanitize(catName)}</span>
          <span class="user-item-cat">${DOMPurify.sanitize(providerLabel)} | ${DOMPurify.sanitize(tierLabel)} | ${ragLabel}</span>
          <span class="user-item-cat">Вход: ${DOMPurify.sanitize(inputDefault)} / ${DOMPurify.sanitize(inputMax)} токенов | Выход: ${DOMPurify.sanitize(outputMax)} токенов</span>
        </div>
        <div class="user-item-actions">
          <button class="nav-btn btn-action-sm edit-cat-btn" data-cat="${DOMPurify.sanitize(catName)}" aria-label="Редактировать ${DOMPurify.sanitize(catName)}">✏️</button>
        </div>
      `;
      list.appendChild(el);
    });

    document.querySelectorAll('.edit-cat-btn').forEach(b => b.addEventListener('click', (e: Event) => {
      const btn = (e.target as Element).closest('button') as HTMLButtonElement;
      const c = btn.dataset.cat || '';
      editAdminCategory(c, cats[c]);
    }));
  }
}

async function loadProvidersList() {
  if (availableProviders.length > 0) return;
  try {
    const r = await fetch('/api/providers', { headers: { 'Authorization': 'Bearer ' + state.authToken } });
    if (r.ok) availableProviders = await r.json();
  } catch (e) { console.error('Failed to load providers:', e); }
}

function populateProviderDropdown(selectedId: string) {
  const sel = $<HTMLSelectElement>('admin-cat-provider');
  if (!sel) return;
  sel.textContent = '';
  availableProviders.forEach(p => {
    const opt = document.createElement('option');
    opt.value = p.id;
    opt.textContent = p.name;
    if (p.id === selectedId) opt.selected = true;
    sel.appendChild(opt);
  });
}

function updateProviderUI(providerId: string) {
  const cfg: ProviderInfo = availableProviders.find(p => p.id === providerId) || { id: providerId, name: providerId };
  const isLlama = cfg.adapter === 'llamacpp' || providerId === 'llamacpp';
  document.querySelectorAll('.llama-only-param').forEach(el => {
    el.classList.toggle('hidden', !isLlama);
  });

  const isResponses = cfg.adapter === 'openai_responses' || providerId === 'openai_responses';

  const sysPromptEl = $<HTMLTextAreaElement>('admin-cat-system-prompt');
  if (sysPromptEl) {
    sysPromptEl.disabled = false;
    sysPromptEl.style.opacity = '1';
    if (isResponses) {
      sysPromptEl.placeholder = 'Опциональный системный промпт (будет передан в OpenAI Responses)...';
    } else {
      sysPromptEl.placeholder = 'Опциональный системный промпт для этой категории...';
    }
  }
}

async function editAdminCategory(name: string, data: CategoryData) {
  await loadProvidersList();
  currentEditCategory = name;
  $('admin-cat-edit-card')?.classList.remove('hidden');
  const titleEl = $('admin-cat-edit-title');
  if (titleEl) titleEl.textContent = name ? 'Категория: ' + name : 'Новая категория';

  const nameEl = $<HTMLInputElement>('admin-cat-name');
  if (nameEl) {
    nameEl.value = name || '';
    nameEl.disabled = !!name;
  }
  $('btn-del-cat')?.classList.toggle('hidden', !name);

  const providerId = data.provider || 'llamacpp';
  populateProviderDropdown(providerId);
  updateProviderUI(providerId);

  const sysPromptEl = $<HTMLTextAreaElement>('admin-cat-system-prompt');
  if (sysPromptEl) sysPromptEl.value = (data as CategoryData & { system_prompt?: string }).system_prompt || '';

  const sortIndexEl = $<HTMLInputElement>('admin-cat-sort-index');
  if (sortIndexEl) sortIndexEl.value = String(data.sort_index != null ? parseInt(String(data.sort_index), 10) : 0);
  const suggestedEl = $<HTMLTextAreaElement>('admin-cat-suggested-questions');
  if (suggestedEl) suggestedEl.value = data.suggested_questions || '';

  const debugEl = $<HTMLInputElement>('admin-cat-debug-mode');
  if (debugEl) debugEl.checked = !!(data.debug_mode);

  const ragEl = $<HTMLInputElement>('admin-cat-rag-allowed');
  if (ragEl) ragEl.checked = data.rag_allowed !== false && data.rag_allowed !== 0;

  const tierEl = $<HTMLSelectElement>('admin-cat-retrieval-tier');
  if (tierEl) tierEl.value = data.retrieval_tier || 'consultant';

  // Флаги читаем прямо из полей категории: с миграции admin/001_category_flags
  // это колонки, а не ключи JSON-свалки extra_params, которую форма и не умела
  // редактировать.
  const globalKbEl = $<HTMLInputElement>('admin-cat-global-kb-enabled');
  if (globalKbEl) {
    globalKbEl.checked = data.global_kb_enabled !== false && data.global_kb_enabled !== 0;
  }

  const includeSourcesEl = $<HTMLInputElement>('admin-cat-include-sources');
  if (includeSourcesEl) includeSourcesEl.checked = !!data.include_sources;

  const semanticGraphEl = $<HTMLInputElement>('admin-cat-semantic-graph-enabled');
  if (semanticGraphEl) {
    semanticGraphEl.checked = data.semantic_graph_enabled === true || data.semantic_graph_enabled === 1;
  }

  const complexityEl = $<HTMLInputElement>('admin-cat-complexity');
  if (complexityEl) complexityEl.value = parseFloat(String(data.complexity ?? 1.0)).toFixed(2);

  const inputDefaultEl = $<HTMLInputElement>('admin-cat-input-context-default');
  if (inputDefaultEl) inputDefaultEl.value = String(data.input_context_default != null ? data.input_context_default : 1000000);
  const inputMaxEl = $<HTMLInputElement>('admin-cat-input-context-max');
  if (inputMaxEl) inputMaxEl.value = String(data.input_context_max != null ? data.input_context_max : 1000000);
  const maxTokensEl = $<HTMLInputElement>('admin-cat-max-tokens');
  if (maxTokensEl) maxTokensEl.value = String(data.max_tokens != null ? data.max_tokens : TOKEN_LIMIT_STEP);

  $('admin-cat-edit-card')?.scrollIntoView({ behavior: 'smooth' });
}

$('admin-cat-provider')?.addEventListener('change', (e: Event) => {
  const pid = (e.target as HTMLSelectElement).value;
  updateProviderUI(pid);
  const cfg = availableProviders.find(p => p.id === pid);
  if (cfg?.defaultModel && cfg.defaultModel !== 'default') {
    showToast(`Модель категории будет: ${cfg.defaultModel} (из конфига провайдера)`, 'info');
  }
});

$('btn-cancel-cat')?.addEventListener('click', (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  $('admin-cat-edit-card')?.classList.add('hidden');
});

$('btn-create-cat')?.addEventListener('click', (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  editAdminCategory('', {});
});

$('btn-del-cat')?.addEventListener('click', async (e: MouseEvent) => {
  e.preventDefault();
  if (!currentEditCategory) return;
  if (!confirm(`Удалить категорию ${currentEditCategory}?`)) return;

  const r = await fetch('/api/admin/categories/' + encodeURIComponent(currentEditCategory), {
    method: 'DELETE',
    headers: { 'Authorization': 'Bearer ' + state.authToken }
  });
  if (r.ok) {
    showToast('🗑️ Категория удалена');
    $('admin-cat-edit-card')?.classList.add('hidden');
    loadAdminCategories();
  } else {
    const err = await r.json() as { detail?: string };
    showToast('❌ Ошибка: ' + (err.detail || 'Ошибка удаления'));
  }
});

$('btn-save-cat')?.addEventListener('click', async (e: MouseEvent) => {
  e.preventDefault();
  const catName = $<HTMLInputElement>('admin-cat-name')?.value.trim() || '';
  if (!catName) {
    return showToast('❌ Название категории обязательно');
  }

  const inputContextDefault = readCategoryTokenLimit('admin-cat-input-context-default', 'Входной контекст по умолчанию', 1000000);
  const inputContextMax = readCategoryTokenLimit('admin-cat-input-context-max', 'Максимум входного контекста', 1000000);
  const maxTokens = readCategoryTokenLimit('admin-cat-max-tokens', 'Максимум выходной генерации', TOKEN_LIMIT_STEP);
  if (inputContextDefault === undefined || inputContextMax === undefined || maxTokens === undefined) return;
  if (inputContextDefault > inputContextMax) {
    return showToast('❌ Входной контекст по умолчанию не может быть больше максимума входного контекста');
  }

  const payload = {
    provider: $<HTMLSelectElement>('admin-cat-provider')?.value || 'llamacpp',
    system_prompt: $<HTMLTextAreaElement>('admin-cat-system-prompt')?.value || null,
    debug_mode: !!($<HTMLInputElement>('admin-cat-debug-mode')?.checked),
    rag_allowed: !!($<HTMLInputElement>('admin-cat-rag-allowed')?.checked),
    retrieval_tier: $<HTMLSelectElement>('admin-cat-retrieval-tier')?.value || 'consultant',
    complexity: (() => {
      const raw = parseFloat(parseFloat($<HTMLInputElement>('admin-cat-complexity')?.value || '1').toFixed(2));
      return Number.isFinite(raw) ? raw : 1.0;
    })(),
    input_context_default: inputContextDefault,
    input_context_max: inputContextMax,
    max_tokens: maxTokens,
    sort_index: parseInt($<HTMLInputElement>('admin-cat-sort-index')?.value || '0', 10),
    suggested_questions: $<HTMLTextAreaElement>('admin-cat-suggested-questions')?.value || '',
    // Поля-колонки вместо прежней JSON-свалки extra_params: форма шлёт ровно то,
    // что показывает, и больше не может стереть настройку, которой в ней нет.
    global_kb_enabled: !!($<HTMLInputElement>('admin-cat-global-kb-enabled')?.checked),
    include_sources: !!($<HTMLInputElement>('admin-cat-include-sources')?.checked),
    semantic_graph_enabled: !!($<HTMLInputElement>('admin-cat-semantic-graph-enabled')?.checked),
  };

  const r = await fetch('/api/admin/categories/' + encodeURIComponent(catName), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + state.authToken },
    body: JSON.stringify(payload)
  });
  if (r.ok) {
    showToast('✅ Категория сохранена');
    $('admin-cat-edit-card')?.classList.add('hidden');
    loadAdminCategories();
  } else {
    try {
      const errData = await r.json() as ApiErrorBody;
      const errMsg = errData.detail || (errData.error && errData.error.message) || errData.message || JSON.stringify(errData);
      showToast('❌ Ошибка сохранения: ' + errMsg);
    } catch {
      showToast('❌ Ошибка сохранения');
    }
  }
});

$('btn-test-cat')?.addEventListener('click', async (e: MouseEvent) => {
  e.preventDefault();
  e.stopPropagation();
  const catName = $<HTMLInputElement>('admin-cat-name')?.value.trim() || '';
  if (!catName) return showToast('❌ Сначала сохраните категорию');
  const btn = $<HTMLButtonElement>('btn-test-cat');
  if (!btn) return;
  const oldText = btn.textContent;
  btn.textContent = '⏳ Проверка...';
  btn.disabled = true;

  try {
    const payload = {
      provider: $<HTMLSelectElement>('admin-cat-provider')?.value,
    };

    const r = await fetch(`/api/admin/categories/${encodeURIComponent(catName)}/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + state.authToken
      },
      body: JSON.stringify(payload)
    });
    const d = await r.json() as { message?: string; error?: string };
    if (r.ok) {
      showToast('✅ ' + d.message);
    } else {
      showToast('❌ ' + (d.error || 'Ошибка соединения'));
    }
  } catch {
    showToast('❌ Ошибка сети при проверке');
  } finally {
    btn.textContent = oldText;
    btn.disabled = false;
  }
});

// bindGroupedNumberInput сохранён как в исходном admin.ts: сейчас нигде не
// вызывается, но остаётся частью формы лимитов категории.
export { bindGroupedNumberInput };
